import React from "react";
import { ArrowRight } from "lucide-react";
import { useLocale } from "../../i18n/LocaleContext";

const PERSONAS = [
  {
    role: "Chief Procurement Officer",
    short: "CPO",
    img: "https://images.unsplash.com/photo-1573164713988-8665fc963095?q=80&w=1600&auto=format&fit=crop",
    bullets: [
      "Consolidate 10,000+ tail suppliers into one Vendor of Record",
      "Pre-negotiated rate cards across every indirect category",
      "Audit-ready spend governance with full category coverage",
      "Eliminate maverick spend at plant and BU level",
    ],
  },
  {
    role: "Chief Financial Officer",
    short: "CFO",
    img: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=1600&auto=format&fit=crop",
    bullets: [
      "Capture 12–18% leakage embedded in tail indirect spend",
      "Reduce P2P operating cost: fewer POs, fewer invoices, fewer GRN exceptions",
      "Working capital uplift via consolidated payment terms",
      "Real-time spend dashboards, ESG and DEI supplier reporting",
    ],
  },
  {
    role: "Chief Supply Chain Officer",
    short: "CSO",
    img: "https://images.unsplash.com/photo-1554469384-e58fac16e23a?q=80&w=1600&auto=format&fit=crop",
    bullets: [
      "Resilient, multi-source coverage of OEM and MRO spare parts",
      "Global distribution network reach across 80+ countries",
      "Continuity of supply for plant-critical components",
      "Visibility from PO to GRN — no more email expediting",
    ],
  },
];

export default function Personas() {
  const { t } = useLocale();
  return (
    <section
      id="personas"
      data-testid="personas-section"
      className="bg-white py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-4">
            <span className="inline-block h-px w-8 bg-slate-300" />
            <span>{t("landing.personas.eyebrow")}</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-[56px] font-semibold tracking-[-0.02em] mt-1 text-slate-900 leading-[1.05]">
            {t("landing.personas.headline")}
          </h2>
          <p className="mt-4 text-slate-600 text-lg leading-relaxed font-light">
            {t("landing.personas.body")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6">
          {PERSONAS.map((p, i) => (
            <article
              key={p.short}
              data-testid={`persona-card-${p.short.toLowerCase()}`}
              className="group rounded-2xl bg-white border border-slate-200 hover:border-[#0072CE] hover:shadow-lg transition-all overflow-hidden flex flex-col"
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={p.img}
                  alt={p.role}
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-900/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="text-[#7DD3FC] text-xs font-bold uppercase tracking-[0.2em]">
                    The {p.short}
                  </div>
                  <div className="font-display text-white text-2xl font-bold leading-tight tracking-tight mt-1">
                    {p.role}
                  </div>
                </div>
              </div>
              <div className="p-6 md:p-7 flex-1 flex flex-col">
                <ul className="space-y-3 flex-1">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#0072CE] shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#lead-form"
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector("#lead-form")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  data-testid={`persona-cta-${p.short.toLowerCase()}`}
                  className="mt-6 inline-flex items-center gap-2 text-[#0072CE] hover:text-[#005BA6] font-semibold text-sm"
                >
                  Brief our team on {p.short} priorities <ArrowRight size={16} />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
